import { SOUND_FILES, SOUND_NAMES, type SoundName } from "./sounds";

const MASTER_GAIN = 0.6;
const MAX_CONCURRENT = 2;
const FADE_OUT_SEC = 0.01;

type Playing = { source: AudioBufferSourceNode; gain: GainNode };

/**
 * Thin Web Audio player for short UI sounds: decoded buffers, a master gain, same-sound
 * restart, exclusive celebrations, and an unlock routine for mobile autoplay policies.
 * Web Audio (not <audio>) so the iOS silent switch mutes it like system UI sounds.
 * Never throws: sound is decoration, not a feature the UI may fail on.
 */
export function createSoundPlayer() {
    let context: AudioContext | null = null;
    let master: GainNode | null = null;
    let armed = false;
    const buffers = new Map<SoundName, AudioBuffer>();
    const loading = new Map<SoundName, Promise<void>>();
    const playing = new Map<SoundName, Playing>();

    const getContext = (): AudioContext | null => {
        if (typeof window === "undefined") return null;
        if (context) return context;
        const Ctor =
            window.AudioContext ??
            (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!Ctor) return null;
        try {
            context = new Ctor();
        } catch {
            return null;
        }
        master = context.createGain();
        master.gain.value = MASTER_GAIN;
        master.connect(context.destination);
        // iOS suspends ("interrupted") after calls or backgrounding; the next gesture resumes it.
        context.addEventListener("statechange", () => {
            if (context && context.state !== "running") armUnlock();
        });
        return context;
    };

    const resume = () => {
        const ctx = getContext();
        if (ctx && ctx.state !== "running") void ctx.resume().catch(() => undefined);
    };

    const load = (name: SoundName): Promise<void> => {
        if (buffers.has(name)) return Promise.resolve();
        const pending = loading.get(name);
        if (pending) return pending;
        const ctx = getContext();
        if (!ctx) return Promise.resolve();
        const promise = fetch(SOUND_FILES[name].src)
            .then((response) => response.arrayBuffer())
            .then((data) => ctx.decodeAudioData(data))
            .then((decoded) => {
                buffers.set(name, decoded);
            })
            .catch(() => undefined)
            .finally(() => loading.delete(name));
        loading.set(name, promise);
        return promise;
    };

    const preload = () => Promise.all(SOUND_NAMES.map(load)).then(() => undefined);

    /** Unlock + preload on the next user gesture (capture phase, once). Re-armed whenever the context stops running. */
    const armUnlock = () => {
        if (typeof window === "undefined" || armed) return;
        armed = true;
        const unlock = () => {
            armed = false;
            window.removeEventListener("pointerdown", unlock, true);
            window.removeEventListener("keydown", unlock, true);
            resume();
            void preload();
        };
        window.addEventListener("pointerdown", unlock, { capture: true, once: true, passive: true });
        window.addEventListener("keydown", unlock, { capture: true, once: true, passive: true });
    };

    const stop = (name: SoundName) => {
        const current = playing.get(name);
        if (!current || !context) return;
        playing.delete(name);
        try {
            const now = context.currentTime;
            current.gain.gain.cancelScheduledValues(now);
            current.gain.gain.setValueAtTime(current.gain.gain.value, now);
            current.gain.gain.linearRampToValueAtTime(0, now + FADE_OUT_SEC);
            current.source.stop(now + FADE_OUT_SEC + 0.002);
        } catch {
            // already stopped
        }
    };

    const play = (name: SoundName) => {
        try {
            const ctx = getContext();
            if (!ctx || !master) return;
            if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
            // Called from inside the click's call stack, so this counts as the gesture on mobile.
            resume();
            const buffer = buffers.get(name);
            if (!buffer) {
                void load(name);
                return;
            }
            const spec = SOUND_FILES[name];
            if (spec.exclusive) {
                for (const other of [...playing.keys()]) stop(other);
            } else {
                stop(name);
            }
            while (playing.size >= MAX_CONCURRENT) {
                const oldest = playing.keys().next().value as SoundName | undefined;
                if (oldest === undefined) break;
                stop(oldest);
            }
            const source = ctx.createBufferSource();
            source.buffer = buffer;
            const gain = ctx.createGain();
            gain.gain.value = spec.gain;
            source.connect(gain);
            gain.connect(master);
            source.onended = () => {
                if (playing.get(name)?.source === source) playing.delete(name);
            };
            source.start();
            playing.set(name, { source, gain });
        } catch {
            // never let a sound failure reach the UI
        }
    };

    const stopAll = () => {
        for (const name of [...playing.keys()]) stop(name);
    };

    return { armUnlock, preload, play, stopAll };
}

export type SoundPlayer = ReturnType<typeof createSoundPlayer>;

export const soundPlayer: SoundPlayer = createSoundPlayer();
